import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsLogged } from '../../store/auth/authReducer';
import { duelsHubRequest } from '../../store/duels/duelsActions';
import {
	selectDuelsHub,
	selectDuelsHubErrorKey,
	selectDuelsHubLoading,
} from '../../store/duels/duelsReducer';

const LOAD_RETRY_BASE_MS = 30000;
const LOAD_RETRY_MAX_MS = 300000;
const RESULT_REFRESH_DELAYS_MS = [60000, 120000, 300000];

const DuelsHost = () => {
	const dispatch = useDispatch();
	const isLogged = useSelector(selectIsLogged);
	const hub = useSelector(selectDuelsHub);
	const hubLoading = useSelector(selectDuelsHubLoading);
	const hubErrorKey = useSelector(selectDuelsHubErrorKey);
	const loaded = hub !== null;
	const [loadAttempt, setLoadAttempt] = useState(0);

	useEffect(() => {
		if (!isLogged || loaded || hubLoading || hubErrorKey) return;

		dispatch(duelsHubRequest());
	}, [isLogged, loaded, hubLoading, hubErrorKey, dispatch]);

	useEffect(() => {
		if (loaded) setLoadAttempt(0);
	}, [loaded]);

	useEffect(() => {
		if (!isLogged || loaded || hubLoading || !hubErrorKey) return;

		const backoff = Math.min(LOAD_RETRY_BASE_MS * 2 ** loadAttempt, LOAD_RETRY_MAX_MS);
		const timeout = setTimeout(
			() => {
				setLoadAttempt(attempt => attempt + 1);
				dispatch(duelsHubRequest());
			},
			backoff * (0.8 + Math.random() * 0.4),
		);

		return () => clearTimeout(timeout);
	}, [isLogged, loaded, hubLoading, hubErrorKey, loadAttempt, dispatch]);

	const resultAt = hub?.currentDuel?.currentPlayerFinished ? hub.currentDuel.expiresAt : null;
	const resultRefreshes = useRef({ resultAt: null as string | null, count: 0 });

	useEffect(() => {
		if (resultRefreshes.current.resultAt !== resultAt) {
			resultRefreshes.current = { resultAt, count: 0 };
		}
		if (!isLogged || !resultAt) return;
		const attempt = resultRefreshes.current.count;
		if (attempt >= RESULT_REFRESH_DELAYS_MS.length) return;
		const timeout = setTimeout(
			() => {
				resultRefreshes.current.count = attempt + 1;
				dispatch(duelsHubRequest());
			},
			Math.max(0, new Date(resultAt).getTime() - Date.now()) + RESULT_REFRESH_DELAYS_MS[attempt],
		);
		return () => clearTimeout(timeout);
	}, [isLogged, resultAt, hub, hubErrorKey, dispatch]);

	return null;
};

export default DuelsHost;
